import { EltType } from 'legacy/lut/classes/models/partner360/ELT/EltType'
import { TRevenueSnapshotKeys } from 'legacy/lut/classes/models/partner360/ELT/IELTFinancialData'
import {
  ELT_FORM_SECTION,
  IBalanceOfTrade,
  IELTFormData,
  IRevenue,
  TComplexSectionData,
} from 'legacy/lut/classes/models/partner360/ELT/IELTFormData'
import { getDataOrNil } from 'legacy/lut/components/widgets/public/partner360/ELT/helpers/getDataOrNil'
import { useEffect, useMemo } from 'react'

import { ActualsValueTypes } from 'partner360/models/actualValueTypes'

interface IProps {
  netNdp?: ActualsValueTypes
  formData: IELTFormData
  onChangeComplexFormData: (section: ELT_FORM_SECTION) => (data: TComplexSectionData) => void
  eltType?: EltType
}

const observedRevenueKeys: TRevenueSnapshotKeys[] = ['GrandTotal']

type TObservedItems = Record<
  TRevenueSnapshotKeys,
  {
    PreviousYearRevenue: number
    CurrentYearRevenue: number
    TwoYearsAgo: number
    ThreeYearsAgo: number

    PreviousYearRevenueNet: number
    CurrentYearRevenueNet: number
    TwoYearsAgoNet: number
    ThreeYearsAgoNet: number
  }
>

export const useBoTComputedFields = ({
  onChangeComplexFormData,
  netNdp,
  eltType,
  formData,
}: IProps) => {
  const { Revenue: revenueData, BalanceOfTrade: balanceOfTradeData } = formData

  const deps = [JSON.stringify(revenueData), netNdp]
  const observedItems = useMemo(() => {
    if (!revenueData) {
      return {} as TObservedItems
    }
    return getObservedItemsFromRevenue(revenueData)
  }, deps)

  useEffect(() => {
    if (eltType === EltType.Pptx) {
      if (!revenueData || !balanceOfTradeData) {
        return
      }
      const newBalanceOfTradeData = getNewBalanceOfTradeData(observedItems, balanceOfTradeData)

      onChangeComplexFormData(ELT_FORM_SECTION.BALANCE_OF_TRADE)(newBalanceOfTradeData)
    }
  }, [eltType, observedItems])
}

export const getObservedItemsFromRevenue = (revenueData: IRevenue[]): TObservedItems => {
  const filtered = revenueData.filter((el) => observedRevenueKeys.includes(el.Key))
  return filtered.reduce(
    (
      acc,
      {
        Key,
        PreviousYearRevenue,
        PreviousYearRevenueNet,
        CurrentYearRevenue,
        CurrentYearRevenueNet,
        TwoYearsAgo,
        TwoYearsAgoNet,
        ThreeYearsAgo,
        ThreeYearsAgoNet,
      }
    ) => {
      return {
        ...acc,
        [Key]: {
          PreviousYearRevenue: PreviousYearRevenue,
          CurrentYearRevenue: CurrentYearRevenue,
          ThreeYearsAgo: ThreeYearsAgo,
          TwoYearsAgo: TwoYearsAgo,

          PreviousYearRevenueNet: PreviousYearRevenueNet,
          CurrentYearRevenueNet: CurrentYearRevenueNet,
          ThreeYearsAgoNet: ThreeYearsAgoNet,
          TwoYearsAgoNet: TwoYearsAgoNet,
        },
      } as TObservedItems
    },
    {} as TObservedItems
  )
}

export const getNewBalanceOfTradeData = (
  observedItems: TObservedItems,
  balanceOfTradeData: IBalanceOfTrade[]
) => {
  const totalRevenueIndex = balanceOfTradeData.findIndex((el) => el.Key === 'TotalRevenue')
  const balanceOfTradeIndex = balanceOfTradeData.findIndex((el) => el.Key === 'BalanceOfTrade')
  const hpSpend = balanceOfTradeData.find((el) => el.Key === 'HPSpend')

  const newBalanceOfTradeData = [...balanceOfTradeData]

  const totalRevenue = getTotalRevenue(observedItems)

  newBalanceOfTradeData[totalRevenueIndex] = {
    ...newBalanceOfTradeData[totalRevenueIndex],
    ...totalRevenue,
  }
  newBalanceOfTradeData[balanceOfTradeIndex] = {
    ...newBalanceOfTradeData[balanceOfTradeIndex],
    ...getBalanceOfTrade(totalRevenue, hpSpend),
  }

  return newBalanceOfTradeData
}

const getTotalRevenue = (observedItems: TObservedItems): Partial<IBalanceOfTrade> => {
  return {
    CurrentYearTotal: observedItems.GrandTotal.CurrentYearRevenue,
    PrevYearTotal: observedItems.GrandTotal.PreviousYearRevenue,
    TwoYearsAgoTotal: observedItems.GrandTotal.TwoYearsAgo,
    ThreeYearsAgoTotal: observedItems.GrandTotal.ThreeYearsAgo,

    CurrentYearTotalNet: observedItems.GrandTotal.CurrentYearRevenueNet,
    PrevYearTotalNet: observedItems.GrandTotal.PreviousYearRevenueNet,
    TwoYearsAgoTotalNet: observedItems.GrandTotal.TwoYearsAgoNet,
    ThreeYearsAgoTotalNet: observedItems.GrandTotal.ThreeYearsAgoNet,
  }
}

const getBalanceOfTrade = (
  totalRevenue: Partial<IBalanceOfTrade>,
  hpSpend?: Partial<IBalanceOfTrade>
): Partial<IBalanceOfTrade> => {
  return {
    CurrentYearTotal:
      getDataOrNil(totalRevenue?.CurrentYearTotal) - getDataOrNil(hpSpend?.CurrentYearTotal),
    PrevYearTotal: getDataOrNil(totalRevenue?.PrevYearTotal) - getDataOrNil(hpSpend?.PrevYearTotal),
    TwoYearsAgoTotal:
      getDataOrNil(totalRevenue?.TwoYearsAgoTotal) - getDataOrNil(hpSpend?.TwoYearsAgoTotal),
    ThreeYearsAgoTotal:
      getDataOrNil(totalRevenue?.ThreeYearsAgoTotal) - getDataOrNil(hpSpend?.ThreeYearsAgoTotal),

    CurrentYearTotalNet:
      getDataOrNil(totalRevenue?.CurrentYearTotalNet) - getDataOrNil(hpSpend?.CurrentYearTotalNet),
    PrevYearTotalNet:
      getDataOrNil(totalRevenue?.PrevYearTotalNet) - getDataOrNil(hpSpend?.PrevYearTotalNet),
    TwoYearsAgoTotalNet:
      getDataOrNil(totalRevenue?.TwoYearsAgoTotalNet) - getDataOrNil(hpSpend?.TwoYearsAgoTotalNet),
    ThreeYearsAgoTotalNet:
      getDataOrNil(totalRevenue?.ThreeYearsAgoTotalNet) -
      getDataOrNil(hpSpend?.ThreeYearsAgoTotalNet),
  }
}
